// ///////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ***NOTE***: This file contains a temporary and rough syslog solution until a real/polished version is written
// //////////////////////////////////////////////////////////////////////////////////////////////////////////////

import { System } from "../../common/system";
import { Logger } from "../../clients/logger";
import { SHOW_SYSLOG_CHANNEL } from "./_constants";
import { RouterClient } from "../../clients/routerClient";

interface LogParams {
	leadingBlankLine?: boolean;
	indent?: boolean;
	doubleIndent?: boolean;
}

/**
 * System log, manages syslog window and control output to it
 */
export class SystemLog {
	onErrorMakeSystemManagerVisible = true;

	// will be set from config, but make true in case initial config can't be read
	catastrophicFailure = false;

	/**
	 * Initializes system log and window
	 * @param onErrorMakeSystemManagerVisible if true then will make syslog visible on error
	 */
	public initialize(onErrorMakeSystemManagerVisible: boolean) {
		// delay time in milliseconds before bringing logger window to front
		const BRING_LOG_TO_FRONT_DELAY = 15000;
		// delay time in milliseconds to wait before using router (to avoid warnings)
		const WAIT_FOR_ROUTER = 2000;

		this.onErrorMakeSystemManagerVisible = onErrorMakeSystemManagerVisible;

		const mainWindow = System.Window.getCurrent();

		// Not required but timer gives time for Router to come up so no warnings (router will work either way).
		setTimeout(() => {
			RouterClient.addListener(SHOW_SYSLOG_CHANNEL, () => {
				this.showSystemLog();
			});
		}, WAIT_FOR_ROUTER);

		// Not required but timer gives time for most of workspace to come up then bring system log to front (if it is configured to be visible)
		setTimeout(() => {
			mainWindow.bringToFront();
		}, BRING_LOG_TO_FRONT_DELAY);

		// if system log is closed by user, hide the window (closing it by default will kill this process)
		mainWindow.addEventListener("close-requested", () => {
			if (this.catastrophicFailure) {
				System.exit();
			} else {
				this.hideSystemLog();
			}
		});
	}

	updateElement(id: string, color: string | null, params: LogParams, message: string, other: string) {
		const logger = document.getElementById(id);

		if (logger) {
			let indent = "";
			if (params.leadingBlankLine) {
				logger.innerHTML = `${logger.innerHTML}<br />`;
			}
			if (params.indent) {
				indent = " &nbsp &nbsp &nbsp &nbsp";
			} else if (params.doubleIndent) {
				indent = "&nbsp &nbsp &nbsp &nbsp &nbsp &nbsp &nbsp &nbsp";
			}

			/**
			 * Finsemble's CI system determines that any non-empty, non-null style is an error.
			 * See system_manager.py in e2etesting
			 */
			const style = color ? `style="color: ${color};""` : "";

			logger.innerHTML = `${logger.innerHTML}<span ${style}>${indent}${message}<br /></span>`;
			if (other != "") {
				const text = JSON && JSON.stringify ? JSON.stringify(other) : other;
				logger.innerHTML = `${logger.innerHTML}<span ${style}>${indent}${text}<br /></span>`;
			}
		}
	}

	public log(params: LogParams, message: string, other: string = "") {
		Logger.system.debug("SystemLog.log", params, message, other);
		console.log("SYSTEM LOG", message, other);
		this.updateElement("log", null, params, message, other);
	}

	public warn(params: LogParams, message: string, other: string = "") {
		Logger.system.warn("SystemLog.warn", params, message, other);
		if (this.onErrorMakeSystemManagerVisible) {
			this.showSystemLog();
		}

		console.warn("SYSTEM LOG ERROR", message, other);
		this.updateElement("log", "#DC951F", params, message, other);
	}

	public error(params: LogParams, message: string, other: string = "") {
		Logger.system.error("SystemLog.error", params, message, other);
		if (message.includes("Terminating Boot")) {
			this.catastrophicFailure = true;
		}
		if (this.onErrorMakeSystemManagerVisible) {
			this.showSystemLog();
		} else if (this.catastrophicFailure) {
			System.exit();
		}

		console.error("SYSTEM LOG ERROR", message, other);

		this.updateElement("log", "#F26666", params, message, other);
	}

	public notification(params: LogParams, message: string, other: string = "") {
		Logger.system.debug("SystemLog.notification", params, message, other);
		console.warn("SYSTEM LOG NOTIFICATION", message, other);
		this.updateElement("notification", "#0A8CF4", params, message, other);
	}

	public showSystemLog() {
		const finWindow = System.Window.getCurrent();
		finWindow.show();
		finWindow.bringToFront();
		System.hideSplashScreen();
	}

	public hideSystemLog() {
		const finWindow = System.Window.getCurrent();
		finWindow.hide();
	}
}

const systemLog = new SystemLog();

export default systemLog;
